import { PRODUCT_DURATION_DAYS_MAX, PRODUCT_DURATION_DAYS_MIN } from '@aysar/contracts';
import { useCallback, useState } from 'react';

import {
  type PromoDurationFieldProps,
  type UsePromoDurationFieldLogic,
} from './PromoDurationField.types';

/**
 * What the box shows the moment a counted duration is chosen.
 *
 * Thirty days: long enough to be a real comp, short enough that leaving it
 * untouched is not access the platform is still giving away next year.
 */
const DEFAULT_DAYS = 30;

/**
 * The **three** answers a promo grant's duration has.
 *
 * ## Why three, and why none of them is a blank box
 *
 * `EntitlementGrantsService.periodEndFor` reads `'durationDays' in input`, on
 * purpose and with its own docblock explaining it: an **omitted** key means "the
 * plan's own duration", an explicit **`null`** means "never", and a number means
 * that many days. A default parameter would swallow the explicit `undefined` and
 * collapse two of them — and the collapse is not cosmetic. An App Review account
 * silently given thirty days instead of an open-ended comp expires mid-review,
 * and the rejection that follows says nothing useful.
 *
 * So all three are cards an operator **chooses**. None of them is expressible by
 * leaving a number box empty, and the expensive answer — access with no end — is
 * never the one somebody lands on by saying nothing: a fresh form opens on the
 * plan's own duration.
 *
 * ## Copied from `ValidityDaysField`, which it may not import
 *
 * That control is `modules/Courses`-owned, so W6 forbids the import; what carries
 * over is the reasoning. The mode is **chosen** and never derived from the value,
 * so choosing "never expires" sends `null`, the parent hands `null` straight
 * back, and a `stored ?? DEFAULT` box would have thrown away the 45 the operator
 * typed. The last number chosen is therefore remembered *beside* the value —
 * `days` still reads the stored number whenever there is one, and the memory only
 * answers while the stored value is not a number. It is written from the handlers
 * rather than synced in an effect, which the React Compiler forbids (P21c).
 *
 * The one real difference: this control has a third card, so the memory has to
 * survive a trip through **two** other modes rather than one.
 */
export function usePromoDurationFieldLogic({
  value,
  onChange,
  isDisabled = false,
}: PromoDurationFieldProps): UsePromoDurationFieldLogic {
  const stored = typeof value === 'number' ? value : null;

  const [remembered, setRemembered] = useState(stored ?? DEFAULT_DAYS);
  const days = stored ?? remembered;

  // `null` and `undefined` are two different answers here, so they are told apart
  // explicitly rather than by a `??` that treats them as one absence.
  const mode = stored !== null ? 'days' : value === null ? 'forever' : 'plan';

  const chooseMode = useCallback(
    (next: string) => {
      if (isDisabled) {
        return;
      }

      if (next === 'days') {
        onChange(clamp(days));
        return;
      }

      if (next === 'forever') {
        onChange(null);
        return;
      }

      if (next === 'plan') {
        // `undefined`, which `JSON.stringify` drops — that is how "not chosen"
        // reaches the api as an absent key rather than as a null meaning never.
        onChange(undefined);
      }
    },
    [isDisabled, onChange, days],
  );

  const setDays = useCallback(
    (raw: string) => {
      if (isDisabled) {
        return;
      }

      // An emptied `<input type="number">` reports `''`, and `Number('')` is 0 —
      // which would rewrite the duration to the floor while the operator is
      // half-way through retyping it. Leaving the stored value alone is the
      // honest reading of a box mid-edit.
      if (raw.trim() === '') {
        return;
      }

      const parsed = Number(raw);

      if (!Number.isFinite(parsed)) {
        return;
      }

      const next = clamp(parsed);

      setRemembered(next);
      onChange(next);
    },
    [isDisabled, onChange],
  );

  return {
    mode,
    days,
    isCounted: mode === 'days',
    min: PRODUCT_DURATION_DAYS_MIN,
    max: PRODUCT_DURATION_DAYS_MAX,
    chooseMode,
    setDays,
  };
}

/** Held to the contract's own bounds, so the box cannot offer a 422. */
function clamp(days: number): number {
  return Math.min(Math.max(Math.trunc(days), PRODUCT_DURATION_DAYS_MIN), PRODUCT_DURATION_DAYS_MAX);
}
